const { BaseClient } = require('../shared')
const stringUtils = require('./stringUtils');

let API_URL = process.env.CAREPOINT_URL || process.env.REACT_APP_CAREPOINT_URL;

const DEFAULT_NETWORK = 'n/a';
const DEFAULT_DISTANCE = 20;

class ProviderClient extends BaseClient {
    constructor(providerServiceUrl = API_URL) {
        super(providerServiceUrl);
    }

    networkEncode(network) {
        if (network != null && !Array.isArray(network)) {
            if (typeof network === 'string') {
                network = network.toLowerCase();
            }
        }
        return network;
    }

    /**
    * @param {*} network 
    * @param {*} state 
    * @param {*} city 
    * @param {*} zipCode 
    * @param {*} address1 
    * @param {*} address2 
    * @param {*} practiceName
    */
    generateDoctorKey(network, state, city, zipCode, address1, address2 = null, practiceName) {
        return stringUtils.sanityStringLowerCaseNoSpace(`${network}#${state}#${city}#${zipCode}#`
            + stringUtils.sanityStringNumbersAndLettersSquishedLowerCase(address1 + address2)
            + '#' + practiceName);
    }

    /**
     * @param {*} doctor 
     */
    generateDoctorKeyFromDoctor(doctor) {
        if (doctor == null) {
            return null;
        }

        return this.generateDoctorKey(doctor.network, doctor.state, doctor.city, doctor.zipCode, doctor.address1, doctor.address2, doctor.practiceName);
    }

    /**
     * @param {*} member 
     */
    generateDoctorKeyFromMember(member) {
        if (member == null) {
            return null;
        }

        return this.generateDoctorKey(member.pcpNetwork, member.pcpState, member.pcpCity,
            member.pcpZipCode, member.pcpAddress1, member.pcpAddress2, member.pcpPracticeName);
    }

    /**
     * @param {*} referral 
     * @returns {
     *  referredByKey --> should never be null
     *  referredToKey --> can be null (if specialty only referral)
     * }
     */
    generateDoctorKeyFromReferral(referral) {
        let referredByKey = null;
        let referredToKey = null;
        if (referral == null) {
            return null;
        }

        if (referral.referredByPcpNpi != null) {
            referredByKey = this.generateDoctorKey(referral.referredByPcpNetwork, referral.referredByPcpState, referral.referredByPcpCity,
                referral.referredByPcpZipCode, referral.referredByPcpAddress1,
                referral.referredByPcpAddress2 ? referral.referredByPcpAddress2 : null, referral.referredByPcpPracticeName);
        }
        if (referral.referredToPcpNpi != null) {
            referredToKey = this.generateDoctorKey(referral.referredToPcpNetwork, referral.referredToPcpState, referral.referredToPcpCity,
                referral.referredToPcpZipCode, referral.referredToPcpAddress1,
                referral.referredToPcpAddress2 ? referral.referredToPcpAddress2 : null, referral.referredToPcpPracticeName);
        }

        return {
            referredByKey,
            referredToKey
        }
    }

    async getPracticesByZipCode(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`practice/zipCode`, {
            params,
            ...options
        })

        return response.data
    }

    async getPracticesByPlaceId(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`practice/placeId`, {
            params,
            ...options
        })

        return response.data
    }

    async getProvidersByZipCode(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`provider/zipCode`, {
            params,
            ...options
        })

        return response.data
    }

    async getProvidersByPlaceId(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`provider/placeId`, {
            params,
            ...options
        })

        return response.data
    }

    async getProvidersByPracticeId(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`provider/practiceId`, {
            params,
            ...options
        })

        return response.data
    }

    async getProvidersFetch(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`provider/fetch`, {
            params,
            ...options
        })

        return response.data
    }

    /**
     * @param {*} tin 
     */
    async getMedicalNeighborhoodByTin(tin) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`getMedicalNeighborhoodsByTin`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                tin: tin
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} tins (comma delimited tins)
     */
    async getMedicalNeighborhoodByTins(tins) {
        if (tins == null) {
            return [];
        }

        let tasks = [];
        for (let tin of tins.split(",")) {
            tasks.push(this.getMedicalNeighborhoodByTin(tin));
        }
        let medicalNeighborhoods = await Promise.all(tasks);
        let flatten = [];
        for (let item of medicalNeighborhoods) {
            flatten.push(...item);
        }
        return flatten;
    }

    /**
     * @param {{
     *  memberUuid?: string,
     *  isProvider?: boolean
     *  isMember?: boolean
     *  isCommonSpecialty?: boolean
     *  isFacility?: boolean
     *  isOe?: boolean
     * }} reqParams 
     * @return {Promise<AxiosResponse | null>}
     */
    async getSpecialties(reqParams) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`getSpecialties`, {
            headers: this.DEFAULT_HEADERS,
            params: reqParams
        });

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * @param {{
     *  isOeDoctor: boolean
     *  isOeFacility: boolean
     * }} reqParams 
     */
    async getServiceLines(reqParams) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`getServiceLines`, {
            headers: this.DEFAULT_HEADERS,
            params: reqParams
        });
        if (response.status !== 200) return null;
        return response.data;
    }

    async getFacilityMap() {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`getFacilityMap`, {
            headers: this.DEFAULT_HEADERS,
            params: {
            }
        });

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    async getZipCodeLatAndLong(zipCode) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`getZipCodeLatAndLong`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                zipCode
            }
        });

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    async getAllNetworks() {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`getAllNetworks`, {
            headers: this.DEFAULT_HEADERS,
            params: {
            }
        });

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    }

    /**
     * 
     * @param {*} networkId
     */
    async getProviderNetwork(networkId) {
        await this.createAxiosInstance();

        try {
            const response = await this.axiosInstance.get(`getProviderNetwork`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    networkId: networkId,
                }
            });

            if (response.status !== 200) {
                return null;
            }

            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    /**
     * @param {*} network 
     * @param {*} state 
     * @param {*} city 
     * @param {*} zipCode 
     * @param {*} address1 
     * @param {*} address2
     */
    generateFacilityKey(network, state, city, zipCode, address1, address2 = null, specialtyType) {
        // remove all space from network
        // network = network.replace(/ /g, '');
        // TODO: remove old, return `${network}#${state}#${city}#${zipCode}#${address1}#${address2}#${specialtyType}`.toLowerCase();
        return utils.sanityStringLowerCaseNoSpace(`${network}#${state}#${city}#${zipCode}#`
            + utils.sanityStringNumbersAndLettersSquishedLowerCase(address1 + address2)
            + '#' + specialtyType);
    }

    /**
     * @param {*} facility 
     */
    generateFacilityKeyFromFacility(facility) {
        if (facility == null) {
            return null;
        }
        return this.generateFacilityKey(facility.network, facility.state, facility.city, facility.zipCode, facility.address1,
            facility.address2, facility.specialtyType);
    }

    async getFacilitiesByPlaceId(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`facility/opensearch/placeId`, {
            params,
            ...options
        })

        return response.data
    }

    async getFacilitiesByZipCode(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`facility/opensearch/zipCode`, {
            params,
            ...options
        })

        return response.data
    }

    async getFacilitiesByCoords(params, options) {
        await this.createAxiosInstance();

        const response = await this.axiosInstance.get(`facility/coords`, {
            params,
            ...options
        })

        return response.data
    }

    // UTILITY METHODS //

    /**
     * @param {*} specialtyList 
     * @param {*} specialty 
     */
    getSpecialtyDisplay(specialtyList, specialty) {
        if (stringUtils.isStringNumber(specialty) || specialty.toString().length === 2) {
            let result = specialtyList.filter((item) => item.code.toString() === specialty.toString());
            if (result.length > 0) {
                return result[0].specialty;
            }
        }
        return null;
    };

    /**
     * @param {*} doctor 
     * @param {*} virtualOnlyOptions
     */
    stripVirtualCareInformation(doctor, virtualOnlyOptions = ['only']) {
        // Deferred from using the most recent syntax on this because of backwards compatibility with older versions of ES 
        if (doctor == null || !doctor.virtualOption || !virtualOnlyOptions.includes(doctor.virtualOption.toLowerCase())) {
            return doctor;
        }
        doctor.address1 = "";
        doctor.address2 = "";
        doctor.zipCode = "";
        doctor.state = "";
        doctor.county = "";
        doctor.city = "";
        return doctor;
    }
}

module.exports = {
    ProviderClient
}